import { AuthorizationWrapper } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { CommonMessage } from '@frontend/lang';
import { type ReactNode } from 'react';

import OverviewHeader from '../../applications/header/overview-header';
import Unauthorized from '../../unauthorized/unauthorized';
import { TagFilterContainer } from '../card-tag-filter-container/tag-filter-container.component';
import { HardwareInsightsPage } from './hardware/hardware-insights.page';
import { INSIGHTS_SCROLL_BODY_CLASSNAME, INSIGHTS_SHELL_CLASSNAME } from './insights-layout';
import useInsightsOverview from './insights.controller';
import { LendingInsightsPage } from './lending/lending-insights.page';
import { NivalizInsightsPage } from './nivaliz/nivaliz-insights.page';
import { PudoInsightsPage } from './pudo/pudo-insights.page';
import { VendingInsightsPage } from './vending/vending-insights.page';

const INSIGHT_TAB_DISPLAY_MAP = new Map<string, ReactNode>([
    [Application.PUDO, getApplicationDisplayName(Application.PUDO)],
    [Application.VENDING, getApplicationDisplayName(Application.VENDING)],
    [Application.LENDING, getApplicationDisplayName(Application.LENDING)],
    [Application.NIVALIZ, getApplicationDisplayName(Application.NIVALIZ)],
    [Application.HARDWARE, getApplicationDisplayName(Application.HARDWARE)]
]);

const ID = 'insight-overview';

const renderActiveInsightsPage = (activeTab: string | null, accountId: string) => {
    switch (activeTab) {
        case Application.PUDO:
            return <PudoInsightsPage accountId={accountId} />;
        case Application.VENDING:
            return <VendingInsightsPage accountId={accountId} />;
        case Application.LENDING:
            return <LendingInsightsPage accountId={accountId} />;
        case Application.NIVALIZ:
            return <NivalizInsightsPage accountId={accountId} />;
        case Application.HARDWARE:
            return <HardwareInsightsPage accountId={accountId} />;
        default:
            return null;
    }
};

const InsightsOverview = () => {
    const viewProps = useInsightsOverview();

    const title = (
        <div className='inline-flex gap-2'>
            {getApplicationDisplayName(Application.ANALYTICS)} / {CommonMessage.OBJECTS.INSIGHT.PLURAL}
        </div>
    );

    if (!viewProps.isReady) {
        return (
            <div
                className={INSIGHTS_SHELL_CLASSNAME}
                id={ID}>
                <div className='w-full shrink-0'>
                    <OverviewHeader title={title} />
                </div>
                <div className={INSIGHTS_SCROLL_BODY_CLASSNAME}>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div
            className={INSIGHTS_SHELL_CLASSNAME}
            id={ID}>
            <AuthorizationWrapper
                showUnauthorized
                unauthorizedContent={<Unauthorized />}
                requiredPermissions={[{ item: EntityType.REPORT, source: EntityType.ACCOUNT, right: Rights.READ, uid: viewProps.currentAccount }]}>
                <div className='w-full shrink-0'>
                    <OverviewHeader title={title} />
                </div>
                {viewProps.showTabs && (
                    <div className='w-full shrink-0'>
                        <TagFilterContainer
                            tabs={viewProps.tabs}
                            onChange={viewProps.onChange}
                            activeTab={viewProps.activeTab ?? ''}
                            displayMap={INSIGHT_TAB_DISPLAY_MAP}
                        />
                    </div>
                )}
                <div className={INSIGHTS_SCROLL_BODY_CLASSNAME}>{renderActiveInsightsPage(viewProps.activeTab, viewProps.currentAccount)}</div>
            </AuthorizationWrapper>
        </div>
    );
};

export default InsightsOverview;
